import React from "react";
import "./HomeView.scss";
import {getTranslate, Translate, TranslateFunction} from "react-localize-redux";
import {connect} from "react-redux";
import {history, Nullable} from "@data-force/app";
import {Button, Empty} from "antd";
import classNames from "classnames";
import StoreState from "../../interfaces/store/storeState";
import {JobEntity} from "../../interfaces/store/actions/payload/job";
import {ClearHomeDataAction, GetHomeDataAction} from "../../interfaces/store/actions/home";
import {clearHomeData, getHomeData} from "../../store/actions/home";
import {FilterType, ContentType} from "../../enums/enums";
import Spinner from "../../components/common/Spinner/Spinner";
import HistoryParams from "../../interfaces/historyParams";
import {formatString, getHistoryParams} from "../../utils/common";
import {
    getJobPhaseWorkflowDefinitions,
    getPhaseConfigList,
    setActiveJobType,
    setFilterType,
    setSelectedJob,
    showDetailsDrawer,
} from "../../store/actions/job";
import {
    GetJobPhaseWorkflowDefinitionsAction,
    GetPhaseConfigListAction,
    SetActiveJobTypeAction,
    SetFilterTypeAction,
    SetSelectedJobAction,
    ShowDetailsDrawerAction,
} from "../../interfaces/store/actions/job";
import {NoAnnouncementsIcon} from "../../assets/Icons";
import Announcement from "../../components/Announcement/Announcement";
import {AnnouncementData} from "../../interfaces/store/actions/payload/home";
import {CLICK_EVENT, DrawerTabs, ENTER_KEY} from "../../utils/constants";
import ContentEmpty from "../../components/common/ContentEmpty/ContentEmpty";
import CardItem from "../../components/CardItem/CardItem";

const {AVAILABLE, IN_PROGRESS, COMPLETED}: typeof ContentType = ContentType;
const {CLAIMABLE, ONGOING, DONE}: typeof FilterType = FilterType;

interface StateProps {
    availableJobs: JobEntity[]
    inProgressJobs: JobEntity[]
    announcements: AnnouncementData[]
    isLoading: boolean
    translate: TranslateFunction
}

interface DispatchProps {
    getHomeData(): GetHomeDataAction

    clearHomeData(): ClearHomeDataAction

    setActiveJobType(payload: string): SetActiveJobTypeAction

    setFilterType(payload: string): SetFilterTypeAction

    getPhaseConfigList(): GetPhaseConfigListAction

    showDetailsDrawer(payload: string): ShowDetailsDrawerAction

    setSelectedJob(payload: Nullable<JobEntity>): SetSelectedJobAction

    getJobPhaseWorkflowDefinitions(): GetJobPhaseWorkflowDefinitionsAction
}

type Props = StateProps & DispatchProps;

class HomeView extends React.Component<Props> {
    private static readonly translationPath: string = "pages.HomeView.HomeView";

    constructor(props: Props) {
        super(props);

        this.routeTo = this.routeTo.bind(this);
        this.jobsCounter = this.jobsCounter.bind(this);
        this.showAnnouncements = this.showAnnouncements.bind(this);
    }

    componentDidMount(): void {
        this.props.getHomeData();
        this.props.getPhaseConfigList();
        this.props.getJobPhaseWorkflowDefinitions();
        this.props.setFilterType(CLAIMABLE);
        this.props.setActiveJobType(AVAILABLE);
    }

    componentWillUnmount(): void {
        this.props.clearHomeData();
    }

    /**
     * Depending on number available jobs, return two or less for presentation
     * @param {JobEntity[]} availableJobs
     * @return {React.ReactNode}
     */
    getAvailableJobs(availableJobs: JobEntity[]): React.ReactNode {
        const jobs: JobEntity[] = availableJobs.slice(0, 2);

        return (
            <div className="df-home-section-available-jobs">
                {jobs.map((job: JobEntity): React.ReactNode => (
                    <div
                        role="button"
                        tabIndex={0}
                        className="df-home-job-item-wrapper"
                        key={job.id}
                        onClick={this.redirectJobItem.bind(this, job, AVAILABLE)}
                        onKeyDown={this.redirectJobItem.bind(this, job, AVAILABLE)}
                    >
                        <CardItem data={job} hideBtnDropdown={false}/>
                    </div>
                ))}
            </div>
        );
    }

    /**
     * Based on job type, we return job item with clickable wrapper that handles redirect to open task drawer on jobs view
     * @param {JobEntity} data
     * @param {string} jobType
     * @param {boolean} hideBtnDropdown
     * @return {React.ReactNode}
     */
    getJobItem(data: JobEntity, jobType: string, hideBtnDropdown: boolean): React.ReactNode {
        return (
            <div
                role="button"
                tabIndex={0}
                className="df-home-job-item-wrapper"
                onClick={this.redirectJobItem.bind(this, data, jobType)}
                onKeyDown={this.redirectJobItem.bind(this, data, jobType)}
            >
                <CardItem data={data} hideBtnDropdown={hideBtnDropdown}/>
            </div>
        );
    }

    /**
     * Redirect user to jobs view with selected job and opened task drawer for that selected job
     * @param {JobEntity} data
     * @param {string} jobType
     * @param {React.MouseEvent | React.KeyboardEvent} event
     */
    redirectJobItem(data: JobEntity, jobType: string, event: React.MouseEvent | React.KeyboardEvent): void {
        const {key, type, currentTarget} = event as React.KeyboardEvent;
        if (currentTarget === document.activeElement && (type === CLICK_EVENT || key === ENTER_KEY)) {
            this.routeTo(jobType);
            this.props.setSelectedJob(data);
            this.props.showDetailsDrawer(DrawerTabs.DETAILS);
        }
    }

    /**
     * List announcements if there are any, otherwise show placeholder text
     * @param {AnnouncementData[]} data
     * @return {React.ReactNode}
     */
    showAnnouncements(data: AnnouncementData[]): React.ReactNode {
        return data.length ? data.map((current: AnnouncementData, index: number): React.ReactNode => (
            <Announcement key={index} data={current}/>
        )) : (
            <Empty
                image={<NoAnnouncementsIcon/>}
                description={<Translate id={`${HomeView.translationPath}.noAnnouncementsYet.text`}/>}
            />
        );
    }

    /**
     * Based on home job type section, we activate job filter type and and active job type,
     * after that we re-route to active job type tab on job view
     * @param {string} jobType
     */
    routeTo(jobType: string): void {
        switch (jobType) {
            case AVAILABLE:
                this.props.setFilterType(CLAIMABLE);
                break;
            case IN_PROGRESS:
                this.props.setFilterType(ONGOING);
                break;
            case COMPLETED:
                this.props.setFilterType(DONE);
                break;
            default:
                break;
        }
        this.props.setActiveJobType(jobType);
        const historyParams: Nullable<HistoryParams> = getHistoryParams();
        history.push(`/${historyParams?.idpId}/jobs`);
    }

    /**
     * Returns “view all” template with the number of available jobs, if the number of jobs is 10 or more, template displays “10+”
     * @param {JobEntity[]} jobs
     * @returns {string}
     */
    jobsCounter(jobs: JobEntity[]): string {
        const availableJobs: string = jobs.length < 10 ? jobs.length.toString() : "10+";

        return formatString(this.props.translate(`${HomeView.translationPath}.viewAll.text`) as string, availableJobs);
    }

    render(): React.ReactNode {
        const {translationPath}: typeof HomeView = HomeView;
        const {
            availableJobs,
            inProgressJobs,
            announcements,
            isLoading,
        }: Props = this.props;
        const availableJobsClasses: string = classNames("df-home-available-jobs", {
            "df-home-empty-container": !availableJobs.length && !inProgressJobs.length,
        });

        return (
            <div className="df-home-view">
                {isLoading && (<Spinner/>)}
                <section className="df-home-content">
                    <div className="df-home-headline">
                        <h2><Translate id={`${translationPath}.headlineTitle.text`}/></h2>
                        <p><Translate id={`${translationPath}.headlineContent.text`}/></p>
                    </div>
                    <div className="df-home-jobs">
                        <div className={availableJobsClasses}>
                            <div className="df-home-section-header">
                                <h2><Translate id={`${translationPath}.availableTitle.text`}/></h2>
                                <Button
                                    type="link"
                                    onClick={this.routeTo.bind(this, AVAILABLE)}
                                >
                                    {this.jobsCounter(availableJobs)}
                                </Button>
                            </div>
                            {!isLoading && (
                                availableJobs.length ? this.getAvailableJobs(availableJobs) : <ContentEmpty type={AVAILABLE}/>)}
                        </div>
                        <div className="df-home-in-progress-jobs">
                            <div className="df-home-section-header">
                                <h2><Translate id={`${translationPath}.inProgressTitle.text`}/></h2>
                                <Button
                                    type="link"
                                    onClick={this.routeTo.bind(this, IN_PROGRESS)}
                                >
                                    {this.jobsCounter(inProgressJobs)}
                                </Button>
                            </div>
                            {!isLoading && (inProgressJobs.length
                                ? this.getJobItem(inProgressJobs[0], IN_PROGRESS, true)
                                : <ContentEmpty type={IN_PROGRESS}/>)}
                        </div>

                    </div>
                </section>
                <aside className="df-home-aside">
                    <div className="df-home-announcements">
                        <h2><Translate id={`${translationPath}.announcementsTitle.text`}/></h2>
                        <div className="df-home-announcements-container">
                            {!isLoading && this.showAnnouncements(announcements)}
                        </div>
                    </div>
                </aside>
            </div>
        );
    }
}

function mapStateToProps(state: StoreState): StateProps {
    return {
        availableJobs: state.home.availableJobs,
        inProgressJobs: state.home.inProgressJobs,
        announcements: state.home.announcements,
        isLoading: state.mask.isLoading,
        translate: getTranslate(state.localize),
    };
}

const mapDispatchToProps: DispatchProps = {
    getHomeData,
    clearHomeData,
    setActiveJobType,
    setFilterType,
    getPhaseConfigList,
    showDetailsDrawer,
    setSelectedJob,
    getJobPhaseWorkflowDefinitions,
};

export default connect(mapStateToProps, mapDispatchToProps)(HomeView);
